import { supabase } from '../supabaseClient';

/**
 * ShinyVault behaviour tracking.
 *
 * Two jobs from one stream. Support forensics: pull up one customer by email
 * and read their whole visit in order, so "it didn't work" becomes a trail you
 * can actually look at. And aggregate: where people come from and where they
 * drop off.
 *
 * Writes are fire-and-forget. Tracking must never block a page, never throw
 * into a render, and never stop someone from buying — every call here is
 * wrapped so a failure is silent to the customer and merely absent from the
 * data.
 */

const VISITOR_KEY = 'sv_visitor_id';
const SESSION_KEY = 'sv_session_id';
const ATTRIB_KEY = 'sv_attribution';

const uuid = () =>
  (crypto.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      }));

function safeGet(store, key) {
  try { return store.getItem(key); } catch { return null; }
}
function safeSet(store, key, val) {
  try { store.setItem(key, val); } catch { /* private mode */ }
}

// Stable across visits. Anonymous until they tell us who they are.
function visitorId() {
  let v = safeGet(localStorage, VISITOR_KEY);
  if (!v) { v = uuid(); safeSet(localStorage, VISITOR_KEY, v); }
  return v;
}

// One visit. A new tab is a new session, which is the right grain for a funnel.
function sessionId() {
  let s = safeGet(sessionStorage, SESSION_KEY);
  if (!s) { s = uuid(); safeSet(sessionStorage, SESSION_KEY, s); }
  return s;
}

// Captured once per session. The referrer is only truthful on the first hit,
// and UTMs vanish from the URL as soon as they navigate, so both are frozen
// here and repeated onto every later row. That is what lets a funnel query
// slice by source without joining back to the first event.
function attribution() {
  const cached = safeGet(sessionStorage, ATTRIB_KEY);
  if (cached) { try { return JSON.parse(cached); } catch { /* fall through */ } }

  const params = new URLSearchParams(window.location.search);
  const ref = document.referrer || '';
  const sameSite = ref && ref.includes(window.location.host);

  const attrib = {
    referrer: sameSite ? '' : ref,
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
    utm_content: params.get('utm_content') || null,
  };
  safeSet(sessionStorage, ATTRIB_KEY, JSON.stringify(attrib));
  return attrib;
}

function device() {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1100) return 'tablet';
  return 'desktop';
}

let lastPath = null;

/**
 * Record one event. Never awaited by callers, never throws.
 *
 * @param {string} type   page_view | product_view | add_to_cart | cart_view |
 *                        checkout_view | order_placed | search | click | error
 * @param {object} fields label, product_id, order_id, value_cents, meta
 */
export function track(type, fields = {}) {
  try {
    const attrib = attribution();
    const row = {
      visitor_id: visitorId(),
      session_id: sessionId(),
      event_type: type,
      path: fields.path ?? window.location.pathname,
      label: fields.label ?? null,
      product_id: fields.product_id ?? null,
      order_id: fields.order_id ?? null,
      value_cents: fields.value_cents ?? null,
      device: device(),
      user_agent: navigator.userAgent?.slice(0, 400) ?? null,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      meta: fields.meta ?? null,
      ...attrib,
    };
    supabase.from('sv_events').insert(row).then(
      () => {},
      () => {},   // a failed beacon is not the customer's problem
    );
  } catch {
    /* tracking must never take the page down with it */
  }
}

/** Page views, deduped so a re-render is not a second visit. */
export function trackPageView(pathname) {
  if (pathname === lastPath) return;
  lastPath = pathname;
  track('page_view', { path: pathname });
}

/**
 * Attach an identity to this whole session, including the anonymous part
 * before they signed in or typed their email at checkout. This is what makes
 * "look up this customer" work at all.
 */
export function identify(email, userId = null) {
  try {
    if (!email) return;
    supabase.rpc('sv_identify', {
      p_session_id: sessionId(),
      p_email: email,
      p_user_id: userId,
    }).then(() => {}, () => {});
  } catch { /* ignore */ }
}

/** Surface a real failure the customer hit, so support sees it in the trail. */
export function trackError(where, message, meta = null) {
  track('error', { label: `${where}: ${String(message).slice(0, 300)}`, meta });
}

export const currentSessionId = sessionId;
